/**
 * Stage 3: Pricing - Experience Creation Wizard
 *
 * Collects pricing and availability information:
 * - Pricing model selection (per-person, flat-group, base+per, base-covers-n)
 * - Base price and per-person pricing
 * - Live pricing calculator
 * - Cancellation policy (optional)
 * - Booking lead time
 *
 * Epic: MVP 1.27 - Experience Creation Wizard
 * Stories: SCRUM-199 to SCRUM-203, SCRUM-1289, SCRUM-1290
 * Location: apps/web/src/components/experiences/creation/stages/Stage3Pricing.jsx
 */

import { useState } from 'react';
import FieldWrapper from '../../../common/forms/FieldWrapper';
import FormActions from '../../../common/forms/FormActions';

/**
 * Stage3Pricing Component
 *
 * @param {Object} props
 * @param {Object} props.formData - Current wizard form data
 * @param {Function} props.updateField - Update single field
 * @param {Function} props.updateFormData - Update multiple fields
 * @param {Function} props.onNext - Navigate to next stage
 * @param {Function} props.onBack - Navigate to previous stage
 * @param {Object} props.validation - Validation errors for current stage
 * @param {boolean} props.isValid - Whether current stage is valid
 */
export default function Stage3Pricing({
  formData,
  updateField,
  updateFormData: _updateFormData,
  onNext,
  onBack,
  validation,
  isValid
}) {
  // Extract current values
  const {
    pricingModel = 'per_person',
    basePriceCents = 0,
    perPersonPriceCents = 0,
    includedCount = 2,
    bookingLeadTime = 24,
    cancellationPolicy = ''
  } = formData;

  // Local state for dollar display (convert from cents)
  const [basePriceDollars, setBasePriceDollars] = useState((basePriceCents / 100).toFixed(2));
  const [perPersonPriceDollars, setPerPersonPriceDollars] = useState((perPersonPriceCents / 100).toFixed(2));
  const [includedCountError, setIncludedCountError] = useState(null);
  const [showHighRateConfirm, setShowHighRateConfirm] = useState(false);

  /**
   * Pricing models with metadata
   */
  const pricingModels = [
    {
      id: 'per_person',
      name: 'Per Person',
      popular: true,
      description: 'Most common for group experiences',
      formula: 'Price × Number of Guests',
      example: '$50 × 4 guests = $200',
      icon: '👤'
    },
    {
      id: 'flat_group',
      name: 'Flat Group Rate',
      popular: false,
      description: 'Single price regardless of group size',
      formula: 'Fixed Price',
      example: '$200 for 1-6 guests',
      icon: '👥'
    },
    {
      id: 'base_plus_per',
      name: 'Base + Per Person',
      popular: false,
      description: 'Base rate plus per-additional-person fee',
      formula: 'Base + (Additional Guests × Per-Person)',
      example: '$100 base + ($30 × 3 guests) = $190',
      icon: '💰'
    },
    {
      id: 'base_covers_n',
      name: 'Base Rate + Per Person',
      popular: false,
      description: 'A flat base rate covers a set number of guests. Additional guests pay a per-person rate.',
      formula: 'Base + max(0, Guests − Included) × Per-Person',
      example: '$500 base covers 2, +$100/extra → 4 guests = $700',
      icon: '🎯'
    }
  ];

  /**
   * Handle pricing model selection
   */
  const handlePricingModelChange = (modelId) => {
    updateField('pricingModel', modelId);
  };

  /**
   * Handle base price change (dollars to cents)
   */
  const handleBasePriceChange = (value) => {
    setBasePriceDollars(value);
    const cents = Math.round(parseFloat(value) * 100) || 0;
    updateField('basePriceCents', cents);
  };

  /**
   * Handle per-person price change (dollars to cents)
   */
  const handlePerPersonPriceChange = (value) => {
    setPerPersonPriceDollars(value);
    const cents = Math.round(parseFloat(value) * 100) || 0;
    updateField('perPersonPriceCents', cents);
  };

  /**
   * Handle included guest count change (SCRUM-1289)
   */
  const handleIncludedCountChange = (value) => {
    const count = parseInt(value) || 0;
    if (count < 1) {
      setIncludedCountError('Included guest count must be at least 1');
    } else {
      setIncludedCountError(null);
    }
    updateField('includedCount', count);
  };

  /**
   * Calculate example pricing for live calculator
   */
  const calculateExamplePrice = (guests) => {
    switch (pricingModel) {
      case 'per_person':
        return perPersonPriceCents * guests;
      case 'flat_group':
        return basePriceCents;
      case 'base_plus_per':
        return basePriceCents + (perPersonPriceCents * (guests - 1));
      case 'base_covers_n':
        return basePriceCents + (Math.max(0, guests - includedCount) * perPersonPriceCents);
      default:
        return 0;
    }
  };

  /**
   * High rate confirmation gate — SCRUM-1290
   * Soft confirmation at $10,000 (1,000,000 cents). No hard block.
   */
  const HIGH_RATE_THRESHOLD_CENTS = 1_000_000;

  const handleNext = () => {
    const hasHighRate = basePriceCents > HIGH_RATE_THRESHOLD_CENTS
      || perPersonPriceCents > HIGH_RATE_THRESHOLD_CENTS;

    if (hasHighRate && !showHighRateConfirm) {
      setShowHighRateConfirm(true);
      return;
    }
    setShowHighRateConfirm(false);
    onNext();
  };

  /**
   * Format cents to dollars
   */
  const formatPrice = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Pricing & Availability</h2>

      <div className="space-y-8">
        {/* Pricing Model Selection - SCRUM-199 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Pricing Model <span className="text-red-500">*</span>
          </label>
          <p className="text-sm text-gray-600 mb-4">
            Choose how you want to price your experience
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {pricingModels.map((model) => (
              <button
                key={model.id}
                type="button"
                onClick={() => handlePricingModelChange(model.id)}
                className={`relative p-4 border-2 rounded-lg text-left transition-all ${
                  pricingModel === model.id
                    ? 'border-emerald-600 bg-emerald-50 shadow-md'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                }`}
              >
                {/* Popular Badge */}
                {model.popular && (
                  <div className="absolute -top-2 -right-2 bg-emerald-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
                    Popular
                  </div>
                )}

                {/* Icon */}
                <div className="text-3xl mb-2">{model.icon}</div>

                {/* Model Name */}
                <div className="font-semibold text-gray-900 mb-1">{model.name}</div>

                {/* Description */}
                <div className="text-xs text-gray-600 mb-3">{model.description}</div>

                {/* Formula */}
                <div className="text-xs font-mono bg-gray-50 p-2 rounded mb-2">
                  {model.formula}
                </div>

                {/* Example */}
                <div className="text-xs text-gray-500">{model.example}</div>

                {/* Selected Checkmark */}
                {pricingModel === model.id && (
                  <div className="absolute top-3 right-3 w-6 h-6 bg-emerald-600 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </button>
            ))}
          </div>
          {validation?.errors?.pricingModel && (
            <p className="text-red-600 text-sm mt-2">{validation.errors.pricingModel}</p>
          )}
        </div>

        {/* Price Inputs - SCRUM-200 */}
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Set Your Pricing</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Base Price (for flat_group, base_plus_per, and base_covers_n) */}
            {(pricingModel === 'flat_group' || pricingModel === 'base_plus_per' || pricingModel === 'base_covers_n') && (
              <FieldWrapper
                label={pricingModel === 'flat_group' ? 'Group Price' : 'Base Price'}
                htmlFor="basePrice"
                required={true}
                error={validation?.errors?.basePriceCents}
                helperText={pricingModel === 'flat_group'
                  ? 'Total price for the entire group'
                  : pricingModel === 'base_covers_n'
                    ? 'Base rate that covers included guests (minimum $10)'
                    : 'Starting price (minimum $10)'}
              >
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
                  <input
                    id="basePrice"
                    type="number"
                    min="10"
                    step="1"
                    value={basePriceDollars}
                    onChange={(e) => handleBasePriceChange(e.target.value)}
                    className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>
              </FieldWrapper>
            )}

            {/* Per-Person Price (for per_person, base_plus_per, and base_covers_n) */}
            {(pricingModel === 'per_person' || pricingModel === 'base_plus_per' || pricingModel === 'base_covers_n') && (
              <FieldWrapper
                label={pricingModel === 'per_person'
                  ? 'Price Per Person'
                  : pricingModel === 'base_covers_n'
                    ? 'Per Additional Person Rate'
                    : 'Per Additional Person'}
                htmlFor="perPersonPrice"
                required={true}
                error={validation?.errors?.perPersonPriceCents}
                helperText={pricingModel === 'per_person'
                  ? 'Price charged per guest (minimum $10)'
                  : pricingModel === 'base_covers_n'
                    ? 'Price for each guest beyond the included count (minimum $10)'
                    : 'Price per guest after first (minimum $10)'}
              >
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
                  <input
                    id="perPersonPrice"
                    type="number"
                    min="10"
                    step="1"
                    value={perPersonPriceDollars}
                    onChange={(e) => handlePerPersonPriceChange(e.target.value)}
                    className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>
              </FieldWrapper>
            )}

            {/* Included Guest Count (for base_covers_n only — SCRUM-1289) */}
            {pricingModel === 'base_covers_n' && (
              <FieldWrapper
                label="Included Guest Count"
                htmlFor="includedCount"
                required={true}
                error={includedCountError || validation?.errors?.includedCount}
                helperText="Number of guests covered by the base rate"
              >
                <input
                  id="includedCount"
                  type="number"
                  min="1"
                  step="1"
                  value={includedCount}
                  onChange={(e) => handleIncludedCountChange(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="2"
                />
              </FieldWrapper>
            )}
          </div>
        </div>

        {/* Live Pricing Calculator - SCRUM-201 */}
        {(basePriceCents >= 1000 || perPersonPriceCents >= 1000) && (
          <div className="bg-gradient-to-br from-emerald-50 to-blue-50 border border-emerald-200 rounded-lg p-6">
            <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              Pricing Calculator
            </h4>
            <p className="text-sm text-gray-600 mb-4">See what guests will pay based on group size:</p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[1, 2, 4, 6].map((guests) => (
                <div key={guests} className="bg-white rounded-lg p-3 border border-gray-200">
                  <div className="text-xs text-gray-600 mb-1">
                    {guests} {guests === 1 ? 'guest' : 'guests'}
                  </div>
                  <div className="text-lg font-bold text-emerald-700">
                    {formatPrice(calculateExamplePrice(guests))}
                  </div>
                  {pricingModel === 'per_person' && (
                    <div className="text-xs text-gray-500 mt-1">
                      {formatPrice(perPersonPriceCents)} × {guests}
                    </div>
                  )}
                  {pricingModel === 'base_plus_per' && guests > 1 && (
                    <div className="text-xs text-gray-500 mt-1">
                      {formatPrice(basePriceCents)} + {formatPrice(perPersonPriceCents)} × {guests - 1}
                    </div>
                  )}
                  {pricingModel === 'base_covers_n' && (
                    <div className="text-xs text-gray-500 mt-1">
                      {guests <= includedCount
                        ? `Covered by base`
                        : `${formatPrice(basePriceCents)} + ${formatPrice(perPersonPriceCents)} × ${guests - includedCount}`}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Booking Lead Time */}
        <div>
          <FieldWrapper
            label="Booking Lead Time"
            htmlFor="bookingLeadTime"
            required={true}
            helperText="How far in advance must guests book?"
          >
            <select
              id="bookingLeadTime"
              value={bookingLeadTime}
              onChange={(e) => updateField('bookingLeadTime', parseInt(e.target.value))}
              className="w-full md:w-1/2 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value={0}>Same day (instant booking)</option>
              <option value={12}>12 hours</option>
              <option value={24}>24 hours (1 day)</option>
              <option value={48}>48 hours (2 days)</option>
              <option value={72}>72 hours (3 days)</option>
              <option value={168}>1 week</option>
            </select>
          </FieldWrapper>
        </div>

        {/* Cancellation Policy - SCRUM-203 */}
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Policies (Optional)</h3>

          <FieldWrapper
            label="Cancellation Policy"
            htmlFor="cancellationPolicy"
            helperText="Set expectations for cancellations and refunds"
          >
            <select
              id="cancellationPolicy"
              value={cancellationPolicy}
              onChange={(e) => updateField('cancellationPolicy', e.target.value)}
              className="w-full md:w-1/2 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="">Select policy (optional)...</option>
              <option value="flexible">Flexible - Full refund up to 24 hours before</option>
              <option value="moderate">Moderate - Full refund up to 5 days before</option>
              <option value="strict">Strict - 50% refund up to 7 days before</option>
              <option value="custom">Custom - Set your own terms</option>
            </select>
          </FieldWrapper>

          {cancellationPolicy && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-900">
                <span className="font-semibold">Note:</span> You can customize your cancellation policy details after publishing.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* High Rate Confirmation Dialog — SCRUM-1290 */}
      {showHighRateConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="high-rate-confirm-dialog">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Confirm High Rate</h3>
            <p className="text-sm text-gray-700 mb-6" data-testid="high-rate-confirm-text">
              This rate is above $10,000. Please confirm this is correct before saving.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setShowHighRateConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                data-testid="high-rate-dismiss-btn"
              >
                Go Back
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowHighRateConfirm(false);
                  onNext();
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700"
                data-testid="high-rate-confirm-btn"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Form Actions */}
      <FormActions
        onCancel={onBack}
        onSubmit={handleNext}
        hasUnsavedChanges={true}
        isSaving={false}
        cancelLabel="Back"
        submitLabel="Continue to Media"
        showSaveDraft={false}
        disableSubmit={!isValid}
        submitVariant="success"
        layout="split"
      />
    </div>
  );
}
